import type { AllocationInfo, OverflowPoolInfo } from '@/types/api'
import { getOverflowDisplayState } from '@/hooks/useOverflowStatus'

interface OverflowStatusProps {
  allocation: AllocationInfo
  pool: OverflowPoolInfo | undefined
}

export function OverflowStatus({ allocation, pool }: OverflowStatusProps) {
  const state = getOverflowDisplayState(allocation, pool)

  if (state.mode === 'exhausted') {
    return null
  }

  const pct = pool?.pct_used ?? 0
  let barColor = 'bg-brand-blue'
  if (pct > 90) barColor = 'bg-brand-red'
  else if (pct > 70) barColor = 'bg-brand-orange'

  if (state.mode === 'active') {
    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-text-muted">Drawing from overflow</span>
          <span className="text-xs font-semibold text-brand-blue">{pct}% cap filled</span>
        </div>
        <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out ${barColor}`}
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 text-xs">
      <span
        className={`w-2 h-2 rounded-full ${pool?.has_capacity ? 'bg-brand-green' : 'bg-gray-300'}`}
      />
      <span className="text-text-muted">
        {pool?.has_capacity
          ? 'Overflow available'
          : pool?.is_over_cap
            ? 'Daily cap reached'
            : 'Overflow depleted'}
        {pool && !pool.is_locked ? ` · ${pct}% filled` : pool?.is_locked ? ' · locked' : ''}
      </span>
    </div>
  )
}
